import React from "react";
import { JSONViewer } from "../../../Common/JSONViewer/JSONViewer";
import FontSizeControl from "../../../Common/FontSizeControl";
import { theme } from "../../../../Themes/Themes";
import { Tooltip, styled } from "@mui/material";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Output";
import DataObjectIcon from "@mui/icons-material/DataObject";

interface DataExampleButtonsProps {
  inputDataExample?: string;
  outputDataExample?: string;
  isEdge?: boolean;
  onFontSizeChange?: (fontSize: number) => void;
  disabled?: boolean;
}

// Custom styled tooltip with super light pink background
const CustomTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme: muiTheme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: theme.custom.lightestGrey, // Use theme's lightest grey with pink tint
    border: `1px solid ${theme.custom.lighterPink}`,
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
    maxWidth: "400px",
    maxHeight: "300px",
    fontSize: "12px",
    overflow: "auto",
    // Override JSON text colors for better readability
    "& .json-string": {
      color: "#059669 !important", // Green for strings
    },
    "& .json-number": {
      color: "#dc2626 !important", // Red for numbers
    },
    "& .json-boolean": {
      color: "#7c3aed !important", // Purple for booleans
    },
    "& .json-null": {
      color: "#6b7280 !important", // Gray for null
    },
    "& .json-property": {
      color: "#1d4ed8 !important", // Blue for property names
    },
    "& .json-punctuation": {
      color: "#374151 !important", // Dark gray for brackets, commas, etc.
    },
    // Additional overrides for any white text
    "& *": {
      color: "#374151 !important",
    },
    // Specific overrides for the JSON viewer content
    "& pre": {
      color: "#374151 !important",
      backgroundColor: "transparent !important",
    },
    "& code": {
      color: "#374151 !important",
      backgroundColor: "transparent !important",
    },
  },
  [`& .MuiTooltip-arrow`]: {
    color: theme.custom.lightestGrey,
    "&:before": {
      border: `1px solid ${theme.custom.lighterPink}`,
    },
  },
}));

export const DataExampleButtons: React.FC<DataExampleButtonsProps> = ({
  inputDataExample,
  outputDataExample,
  isEdge = false,
  onFontSizeChange,
  disabled = false,
}) => {
  // Don't render if no examples are available AND no font size control
  if (!inputDataExample && !outputDataExample && !onFontSizeChange) {
    return null;
  }

  const buttonStyle: React.CSSProperties = {
    border: `1px solid ${theme.custom.lighterPink}`,
    borderRadius: "6px",
    padding: "6px 10px",
    margin: "0 4px",
    fontSize: "11px",
    fontWeight: "bold",
    backgroundColor: theme.custom.lighterPink,
    color: "white",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  // Create tooltip content for input data
  const inputTooltipContent = inputDataExample ? (
    <div>
      <div
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          marginBottom: "8px",
          color: theme.custom.pinkerPink,
        }}
      >
        {isEdge ? "Data Example" : "Input Data Example"}
      </div>
      <JSONViewer jsonString={inputDataExample} title="" maxHeight="250px" />
    </div>
  ) : null;

  // Create tooltip content for output data
  const outputTooltipContent = outputDataExample ? (
    <div>
      <div
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          marginBottom: "8px",
          color: theme.custom.pinkerPink,
        }}
      >
        Output Data Example
      </div>
      <JSONViewer jsonString={outputDataExample} title="" maxHeight="250px" />
    </div>
  ) : null;

  return (
    <div
      style={{
        position: "absolute",
        top: "17px",
        left: "5px",
        right: "5px",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        paddingLeft: "5px",
        paddingTop: "5px",
        paddingBottom: "5px",
        height: "30px",
        border: "1px solid #f0f0f0",
        backgroundColor: "#fafafa",
        borderRadius: "6px",
        zIndex: 10,
      }}
    >
      {/* Input Data Example Button */}
      {inputDataExample && (
        <CustomTooltip
          title={inputTooltipContent}
          arrow
          placement="bottom-start"
          enterDelay={200}
          leaveDelay={200}
        >
          <div
            style={buttonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.custom.lighterPink;
              e.currentTarget.style.backgroundColor = "#fef7f0";
              e.currentTarget.style.color = theme.custom.lighterPink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.custom.lighterPink;
              e.currentTarget.style.backgroundColor = theme.custom.lighterPink;
              e.currentTarget.style.color = "white";
            }}
          >
            {isEdge ? (
              <DataObjectIcon style={{ fontSize: "14px" }} />
            ) : (
              <InputIcon style={{ fontSize: "14px" }} />
            )}
            {isEdge ? "Data example" : "Input example"}
          </div>
        </CustomTooltip>
      )}

      {/* Output Data Example Button - only show for nodes (not edges) */}
      {outputDataExample && !isEdge && (
        <CustomTooltip
          title={outputTooltipContent}
          arrow
          placement="bottom-start"
          enterDelay={200}
          leaveDelay={200}
        >
          <div
            style={buttonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.custom.lighterPink;
              e.currentTarget.style.backgroundColor = "#fef7f0";
              e.currentTarget.style.color = theme.custom.lighterPink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.custom.lighterPink;
              e.currentTarget.style.backgroundColor = theme.custom.lighterPink;
              e.currentTarget.style.color = "white";
            }}
          >
            <OutputIcon style={{ fontSize: "14px" }} />
            Output example
          </div>
        </CustomTooltip>
      )}

      {/* Font Size Control - positioned at the right end */}
      {onFontSizeChange && (
        <div
          style={{
            marginLeft: "auto",
            paddingRight: "5px",
          }}
        >
          <FontSizeControl
            onFontSizeChange={onFontSizeChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default DataExampleButtons;
